/**
 * Centralized Error Handler Utility
 * Provides consistent error handling across all API calls
 */

export const ErrorTypes = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Parse error response and return structured error object
 * @param {Error} error - The error object
 * @returns {Object} - Structured error object with type, message, and data
 */
export const parseError = (error) => {
  let errorType = ErrorTypes.UNKNOWN_ERROR;
  let message = 'An unexpected error occurred';
  let data = null;
  let statusCode = null;

  if (!error.response) {
    // Network error or no response
    errorType = ErrorTypes.NETWORK_ERROR;
    message = error.message || 'Network error. Please check your connection.';
  } else {
    statusCode = error.response.status;
    
    switch (statusCode) {
      case 400:
        errorType = ErrorTypes.VALIDATION_ERROR;
        message = error.response.data?.error || 'Validation error';
        data = error.response.data?.errors || null;
        break;
      case 401:
        errorType = ErrorTypes.UNAUTHORIZED;
        message = 'Unauthorized. Please log in again.';
        break;
      case 403:
        errorType = ErrorTypes.FORBIDDEN;
        message = 'Access denied. You do not have permission.';
        break;
      case 404:
        errorType = ErrorTypes.NOT_FOUND;
        message = 'Resource not found.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = ErrorTypes.SERVER_ERROR;
        message = error.response.data?.error || 'Server error. Please try again later.';
        break;
      default:
        message = error.response.data?.error || 'An error occurred';
    }
  }

  return {
    type: errorType,
    message,
    data,
    statusCode,
    requestId: error.response?.headers?.['x-request-id'],
    raw: error
  };
};

/**
 * Format API response in consistent structure
 * @param {*} result - The API result data
 * @param {boolean} error - Whether an error occurred
 * @param {Object} parsedError - Parsed error object (if error occurred)
 * @returns {Object} - Consistent response object
 */
export const formatResponse = (result = null, error = false, parsedError = null) => {
  return {
    error,
    data: error ? null : result,
    message: error ? parsedError.message : null,
    errorType: error ? parsedError.type : null,
    statusCode: error ? parsedError.statusCode : null,
    ...(error && parsedError.data && { fieldErrors: parsedError.data })
  };
};

/**
 * Log error for debugging (can be connected to external error tracking)
 * @param {string} functionName - Name of the function where error occurred
 * @param {Object} parsedError - Parsed error object
 * @param {*} params - Parameters passed to the function
 */
export const logError = (functionName, parsedError, params = null) => {
  const errorLog = {
    function: functionName,
    timestamp: new Date().toISOString(),
    errorType: parsedError.type,
    message: parsedError.message,
    statusCode: parsedError.statusCode,
    requestId: parsedError.requestId,
    params: params && JSON.stringify(params)
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`[API Error] ${functionName}:`, errorLog);
  }

  // TODO: In production, send to external error tracking service (Sentry, etc.)
  // trackError(errorLog);
};